/**
 * Semantic Scene View projection (ADR-009 §2). A total, read-only view of the
 * native document: every layer appears, in layer-panel order, and every native
 * feature without a semantic mapping appears as a `features[]` entry instead
 * of disappearing. Pixels, asset bytes and font bytes never enter the view.
 */
import {
  CONTENT_LIMITS,
  SEMANTIC_SCENE_FORMAT,
  SEMANTIC_SCENE_VERSION,
  contentLabel,
  contentString,
  type AdjustmentView,
  type AssetRef,
  type DecomposedTransform,
  type EffectView,
  type FeatureNote,
  type FontRef,
  type LayerView,
  type LayerViewBase,
  type Paint,
  type SceneView,
  type SlotView,
  type StrokeView,
  type VectorObjectView,
} from '@maipo/agent-protocol';
import { transformRectBoundsPrecise, type Matrix, type Rect } from '../core/geometry';
import { layerBounds, layerDocumentTransform, type EditorDocument, type GroupLayer, type Layer, type LayerId } from '../core/model';
import { LAYER_STYLE_EXTENSION, layerStyle } from '../core/layer-styles';
import { SMART_FILTERS_EXTENSION } from '../core/smart-filters';
import { SMART_OBJECT_EXTENSION } from '../core/smart-objects';
import { SLOT_EXTENSION_KEY, layerSlot } from '../core/template';
import { ARTBOARDS_EXTENSION, artboards } from '../core/export-resources';
import { GLOBAL_SWATCHES_EXTENSION, TEXT_SWATCH_EXTENSION, globalSwatches } from '../core/global-swatches';
import { matrixToPublicTransform } from '../public-format/adapter';
import type { DocumentSession } from '../core/session';
import type { SolidPaint, VectorObject, VectorStroke } from '../vector/types';

const PUBLIC_ROLE_EXTENSION = 'maipo:public-role';
const PUBLIC_DOCUMENT_EXTENSION = 'maipo:public-document';
const LEGACY_SLOT_KEY = 'vireo:slot';

/** Layer-level keys the projection maps to first-class fields. */
const MAPPED_LAYER_KEYS = new Set([LAYER_STYLE_EXTENSION, SMART_FILTERS_EXTENSION, SMART_OBJECT_EXTENSION, SLOT_EXTENSION_KEY, LEGACY_SLOT_KEY, PUBLIC_ROLE_EXTENSION]);
const MAPPED_TEXT_KEYS = new Set([TEXT_SWATCH_EXTENSION]);
const MAPPED_DOCUMENT_KEYS = new Set([ARTBOARDS_EXTENSION, GLOBAL_SWATCHES_EXTENSION, PUBLIC_DOCUMENT_EXTENSION]);

/** Summaries are fixed strings, never document content. */
const KNOWN_FEATURES: Record<string, { summary: string; editable: FeatureNote['editable'] }> = {
  'maipo:animation': { summary: 'Animation data (carried, not interpreted)', editable: 'preserved-only' },
  'maipo:transform-effect': { summary: 'Live transform effect (edit in the app)', editable: 'visual-only' },
  'maipo:vector-halftone': { summary: 'Halftone effect (edit in the app)', editable: 'visual-only' },
  'maipo:type-on-path': { summary: 'Type on a path (edit in the app)', editable: 'visual-only' },
  'maipo:shaper': { summary: 'Shaper tool construction data (edit in the app)', editable: 'visual-only' },
  'maipo:image-trace': { summary: 'Image trace settings (edit in the app)', editable: 'visual-only' },
  'maipo:trace-topology': { summary: 'Colour trace topology (edit in the app)', editable: 'visual-only' },
  'maipo:colour-trace': { summary: 'Colour trace (edit in the app)', editable: 'visual-only' },
  'maipo:colour-stack': { summary: 'Colour stack (edit in the app)', editable: 'visual-only' },
  'maipo:live-paint-source': { summary: 'Live paint source (edit in the app)', editable: 'visual-only' },
  'maipo:live-paint-face': { summary: 'Live paint face (edit in the app)', editable: 'visual-only' },
  'maipo:knife-cut': { summary: 'Knife cut record (edit in the app)', editable: 'visual-only' },
  'maipo:grid-tool': { summary: 'Grid tool construction data (edit in the app)', editable: 'visual-only' },
  'maipo:gradient-mesh': { summary: 'Gradient mesh (edit in the app)', editable: 'visual-only' },
  'maipo:envelope-distort': { summary: 'Envelope distort (edit in the app)', editable: 'visual-only' },
  'maipo:dynamic-corners': { summary: 'Dynamic corners (edit in the app)', editable: 'visual-only' },
  'maipo:appearance': { summary: 'Appearance stack (edit in the app)', editable: 'visual-only' },
  'maipo:vector-brush': { summary: 'Vector brush (edit in the app)', editable: 'visual-only' },
  'maipo:symbol-instances': { summary: 'Symbol instances (edit in the app)', editable: 'visual-only' },
  'maipo:outline-stroke': { summary: 'Outlined stroke record (edit in the app)', editable: 'visual-only' },
  'maipo:object-groups': { summary: 'Vector object groups (edit in the app)', editable: 'visual-only' },
  'maipo:object-group': { summary: 'Vector object group membership (edit in the app)', editable: 'visual-only' },
  'maipo:image-source-edit': { summary: 'Image source edit record (edit in the app)', editable: 'visual-only' },
  'maipo:perspective-planes': { summary: 'Perspective planes (edit in the app)', editable: 'visual-only' },
  'maipo:active-perspective-plane': { summary: 'Active perspective plane (edit in the app)', editable: 'visual-only' },
  'maipo:notes': { summary: 'Document notes (edit in the app)', editable: 'visual-only' },
  'maipo:layer-comps': { summary: 'Layer comps (edit in the app)', editable: 'visual-only' },
  'maipo:actions': { summary: 'Recorded actions (edit in the app)', editable: 'visual-only' },
  'maipo:tool-presets': { summary: 'Tool presets (edit in the app)', editable: 'visual-only' },
  'maipo:brush-presets': { summary: 'Brush presets (edit in the app)', editable: 'visual-only' },
  'maipo:slices': { summary: 'Export slices (edit in the app)', editable: 'visual-only' },
  'maipo:export-assets': { summary: 'Marked export assets (edit in the app)', editable: 'visual-only' },
  'maipo:alpha-channels': { summary: 'Saved alpha channels (edit in the app)', editable: 'visual-only' },
  'maipo:graphic-styles': { summary: 'Graphic styles (edit in the app)', editable: 'visual-only' },
  'maipo:public-transform': { summary: 'Public scene transform record (carried, not interpreted)', editable: 'preserved-only' },
  'maipo:public-font-ref': { summary: 'Public scene font binding (carried, not interpreted)', editable: 'preserved-only' },
  'maipo:public-extensions': { summary: 'Public scene extensions (carried, not interpreted)', editable: 'preserved-only' },
  'maipo:unknown-fields': { summary: 'Unknown fields from a newer writer (carried, not interpreted)', editable: 'preserved-only' },
  [PUBLIC_DOCUMENT_EXTENSION]: { summary: 'Public resource table (carried, not interpreted)', editable: 'preserved-only' },
};

export function featureNote(key: string, scope?: string): FeatureNote {
  const known = KNOWN_FEATURES[key];
  const summary = known?.summary
    ?? (key.startsWith('maipo:') ? 'Unrecognised Maipo extension (carried, not interpreted)' : 'Third-party extension (carried, not interpreted)');
  return {
    key: contentLabel(key, CONTENT_LIMITS.key),
    summary: scope ? `${summary}; on ${scope}` : summary,
    editable: known?.editable ?? 'preserved-only',
  };
}

export interface SceneViewOptions {
  /** DocRef reported in the view. In-page callers use the default, "local". */
  document?: string;
  /** Maximum nesting depth of inlined children (0 = top level only). Default: unlimited. */
  depth?: number;
  /** Project only this group's descendants. */
  root?: LayerId;
  /** Maximum layers per page. Default 5,000. */
  limit?: number;
  cursor?: string;
  /** Cap for text content (UTF-16 units). Default 2,000. */
  textLimit?: number;
  provenance?: (layerId: LayerId) => LayerViewBase['provenance'] | undefined;
}

export class SceneViewCursorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SceneViewCursorError';
  }
}

function round(value: number): number {
  return Object.is(value, -0) ? 0 : value;
}

function rect(value: Rect | null | undefined): Rect | null {
  if (!value || ![value.x, value.y, value.width, value.height].every(Number.isFinite)) return null;
  return { x: round(value.x), y: round(value.y), width: round(value.width), height: round(value.height) };
}

function decompose(matrix: Matrix): DecomposedTransform | null {
  try {
    const transform = matrixToPublicTransform(matrix);
    return {
      x: transform.x ?? 0,
      y: transform.y ?? 0,
      rotation: transform.rotation ?? 0,
      scaleX: transform.scaleX ?? 1,
      scaleY: transform.scaleY ?? 1,
      skewX: transform.skewX ?? 0,
    };
  } catch {
    return null;
  }
}

function paint(value: SolidPaint): Paint {
  return {
    type: 'solid',
    color: value.color,
    alpha: value.alpha,
    ...(value.swatchId === undefined ? {} : { swatch: value.swatchId }),
    ...(value.tint === undefined ? {} : { tint: value.tint }),
  };
}

function stroke(value: VectorStroke): StrokeView {
  return { paint: paint(value.paint), width: value.width, lineCap: value.lineCap, lineJoin: value.lineJoin, dash: [...value.dash] };
}

function vectorObject(object: VectorObject): VectorObjectView {
  const geometry: VectorObjectView['geometry'] = object.type === 'path'
    ? { nodes: object.geometry.nodes.length, subpaths: object.geometry.subpaths.length, fillRule: object.geometry.fillRule, detail: 'omitted' }
    : object.type === 'rectangle'
      ? { x: object.geometry.x, y: object.geometry.y, width: object.geometry.width, height: object.geometry.height, radius: [object.geometry.radius.topLeft, object.geometry.radius.topRight, object.geometry.radius.bottomRight, object.geometry.radius.bottomLeft] }
      : { x: object.geometry.x, y: object.geometry.y, width: object.geometry.width, height: object.geometry.height };
  const appearance = object.extensions['maipo:appearance'];
  const entries = Array.isArray(appearance) ? appearance.length
    : appearance && typeof appearance === 'object' && Array.isArray((appearance as { entries?: unknown }).entries) ? (appearance as { entries: unknown[] }).entries.length
      : undefined;
  return {
    id: object.id,
    type: object.type,
    name: contentLabel(object.name, CONTENT_LIMITS.name),
    visible: object.visible,
    opacity: object.opacity,
    transform: [...object.transform] as unknown as Matrix,
    fill: object.fill ? paint(object.fill) : null,
    stroke: object.stroke ? stroke(object.stroke) : null,
    geometry,
    ...(entries === undefined ? {} : { appearance: { entries } }),
  };
}

function effects(layer: Layer): EffectView[] {
  const result: EffectView[] = [];
  const style = layerStyle(layer);
  if (style.dropShadow) result.push({ type: 'dropShadow', ...style.dropShadow });
  if (style.stroke) result.push({ type: 'stroke', ...style.stroke });
  const filters = layer.extensions[SMART_FILTERS_EXTENSION];
  if (Array.isArray(filters)) {
    for (const filter of filters as unknown[]) {
      if (!filter || typeof filter !== 'object') continue;
      const record = filter as { id?: unknown; type?: unknown; enabled?: unknown };
      if (typeof record.id !== 'string' || (record.type !== 'tone' && record.type !== 'high-pass' && record.type !== 'displace')) continue;
      result.push({ type: 'smartFilter', id: record.id, filter: record.type, enabled: record.enabled !== false });
    }
  }
  return result;
}

function layerFeatures(layer: Layer): FeatureNote[] {
  const notes = new Map<string, FeatureNote>();
  for (const key of Object.keys(layer.extensions)) if (!MAPPED_LAYER_KEYS.has(key)) notes.set(key, featureNote(key));
  if (layer.kind === 'text') {
    for (const key of Object.keys(layer.text.extensions)) if (!MAPPED_TEXT_KEYS.has(key) && !notes.has(key)) notes.set(key, featureNote(key, 'text'));
  }
  if (layer.kind === 'vector') {
    for (const key of Object.keys(layer.vector.extensions)) if (!notes.has(key)) notes.set(key, featureNote(key, 'vector scene'));
    const objectCounts = new Map<string, number>();
    for (const object of layer.vector.objects) for (const key of Object.keys(object.extensions)) objectCounts.set(key, (objectCounts.get(key) ?? 0) + 1);
    for (const [key, count] of objectCounts) if (!notes.has(key)) notes.set(key, featureNote(key, `${count} vector object${count === 1 ? '' : 's'}`));
  }
  return [...notes.values()];
}

function slotView(layer: Layer): SlotView | undefined {
  const slot = layerSlot(layer);
  if (!slot) return undefined;
  return {
    name: contentLabel(slot.name, CONTENT_LIMITS.name),
    role: slot.role,
    layer: layer.id,
    ...(slot.prompt === undefined ? {} : { prompt: contentLabel(slot.prompt, CONTENT_LIMITS.prompt) }),
  };
}

function maskCoverage(layer: Layer): Rect | undefined {
  const tiles = layer.mask?.tiles;
  if (!tiles) return undefined;
  const entries = tiles.entries();
  if (entries.length === 0) return undefined;
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const entry of entries) {
    const dimensions = tiles.tileDimensions(entry.x, entry.y);
    left = Math.min(left, entry.x * tiles.tileSize);
    top = Math.min(top, entry.y * tiles.tileSize);
    right = Math.max(right, entry.x * tiles.tileSize + dimensions.width);
    bottom = Math.max(bottom, entry.y * tiles.tileSize + dimensions.height);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function adjustmentView(layer: Extract<Layer, { kind: 'adjustment' }>): AdjustmentView {
  const value = layer.adjustment;
  const channels = value.curves ? (['rgb', 'red', 'green', 'blue'] as const).filter((channel) => (value.curves?.[channel].length ?? 0) > 0) : undefined;
  return {
    brightness: value.brightness,
    contrast: value.contrast,
    saturation: value.saturation,
    invert: value.invert,
    ...(value.levels ? { levels: { ...value.levels } } : {}),
    ...(channels ? { curves: { channels: [...channels] } } : {}),
    ...(value.gradientMap ? { gradientMap: { enabled: value.gradientMap.enabled, reverse: value.gradientMap.reverse, stops: value.gradientMap.stops.map((stop) => ({ ...stop })) } } : {}),
    ...(value.blackWhite ? { blackWhite: { ...value.blackWhite } } : {}),
  };
}

export interface LayerPosition { parentId: LayerId | null; index: number; depth: number }

/** One layer, without children. Used by the scene view and by the transaction journal. */
export function projectLayer(document: EditorDocument, layer: Layer, position: LayerPosition, options: { textLimit?: number; provenance?: SceneViewOptions['provenance'] } = {}): LayerView {
  const local = layerBounds(layer);
  const documentMatrix = layerDocumentTransform(document.root, layer.id);
  const role = layer.extensions[PUBLIC_ROLE_EXTENSION];
  const slot = slotView(layer);
  const coverage = maskCoverage(layer);
  const provenance = options.provenance?.(layer.id);
  const base: LayerViewBase = {
    id: layer.id,
    kind: layer.kind,
    name: contentLabel(layer.name, CONTENT_LIMITS.name),
    parentId: position.parentId,
    index: position.index,
    depth: position.depth,
    visible: layer.visible,
    opacity: layer.opacity,
    fillOpacity: layer.fillOpacity,
    blendMode: layer.blendMode,
    locks: { all: layer.locked, pixels: layer.pixelsLocked, position: layer.positionLocked },
    labelColor: layer.color,
    transform: { matrix: [...layer.transform] as unknown as Matrix, decomposed: decompose(layer.transform) },
    bounds: {
      local: rect(local),
      document: local && documentMatrix ? rect(transformRectBoundsPrecise(documentMatrix, local)) : null,
    },
    clipping: layer.clipping,
    ...(layer.mask ? { mask: { id: layer.mask.id, kind: 'raster' as const, enabled: layer.mask.enabled, linked: layer.mask.linked, defaultAlpha: layer.mask.defaultAlpha, ...(coverage ? { coverage } : {}) } } : {}),
    effects: effects(layer),
    ...(typeof role === 'string' ? { role: contentLabel(role, CONTENT_LIMITS.name) } : {}),
    ...(slot ? { slot } : {}),
    features: layerFeatures(layer),
    ...(provenance ? { provenance } : {}),
  };
  switch (layer.kind) {
    case 'group': {
      const smart = layer.extensions[SMART_OBJECT_EXTENSION] as { sourceLayerCount?: unknown } | undefined;
      return {
        ...base,
        kind: 'group',
        expanded: layer.expanded,
        ...(smart && typeof smart === 'object' ? { smartObject: { embedded: true as const, sourceLayerCount: typeof smart.sourceLayerCount === 'number' ? smart.sourceLayerCount : layer.children.length } } : {}),
        childCount: layer.children.length,
      };
    }
    case 'text': {
      const content = contentString(layer.text.value, options.textLimit ?? CONTENT_LIMITS.text);
      const swatch = layer.text.extensions[TEXT_SWATCH_EXTENSION];
      const portable = layer.text.schemaVersion === 2 ? layer.text.portable : undefined;
      return {
        ...base,
        kind: 'text',
        text: {
          engine: layer.text.schemaVersion === 2 ? 'portable' : 'browser',
          content: content.value,
          ...(content.truncated ? { truncated: true } : {}),
          box: { ...layer.text.box },
          style: { ...layer.text.style, family: contentLabel(layer.text.style.family, CONTENT_LIMITS.name), ...(typeof swatch === 'string' ? { swatch } : {}) },
          missingFont: layer.text.missingFont,
          ...(portable ? { portable: { fonts: [...portable.fonts], baseDirection: portable.baseDirection, language: portable.language, overflow: portable.overflow } } : {}),
        },
      };
    }
    case 'image':
      return { ...base, kind: 'image', image: { asset: layer.image.asset, frame: { ...layer.image.frame } } };
    case 'raster':
      return { ...base, kind: 'raster', raster: { width: layer.raster.width, height: layer.raster.height, tileSize: layer.raster.tileSize, nonEmptyTiles: layer.raster.entries().length } };
    case 'vector':
      return { ...base, kind: 'vector', objects: layer.vector.objects.map(vectorObject) };
    case 'adjustment':
      return { ...base, kind: 'adjustment', adjustment: adjustmentView(layer) };
  }
}

interface TraversalItem { layer: Layer; position: LayerPosition; inlineChildren: boolean }

function traverse(parent: GroupLayer, parentId: LayerId | null, depth: number, maxDepth: number, output: TraversalItem[]): void {
  parent.children.forEach((layer, index) => {
    const inlineChildren = layer.kind === 'group' && depth < maxDepth;
    output.push({ layer, position: { parentId, index, depth }, inlineChildren });
    if (layer.kind === 'group' && inlineChildren) traverse(layer, layer.id, depth + 1, maxDepth, output);
  });
}

function parseCursor(cursor: string, revision: number): number {
  const match = /^p1\.(\d+)\.(\d+)$/.exec(cursor);
  if (!match) throw new SceneViewCursorError('Scene view cursor is malformed.');
  if (Number(match[1]) !== revision) throw new SceneViewCursorError('Scene view cursor belongs to a different revision; inspect again.');
  return Number(match[2]);
}

export function collectUntrusted(layers: readonly LayerView[], path: string, into: Set<string>): void {
  for (const layer of layers) {
    into.add(`${path}.name`);
    if (layer.role !== undefined) into.add(`${path}.role`);
    if (layer.slot) {
      into.add(`${path}.slot.name`);
      if (layer.slot.prompt !== undefined) into.add(`${path}.slot.prompt`);
    }
    if (layer.features.length > 0) into.add(`${path}.features[].key`);
    if (layer.kind === 'text') {
      into.add(`${path}.text.content`);
      into.add(`${path}.text.style.family`);
    }
    if (layer.kind === 'vector' && layer.objects.length > 0) into.add(`${path}.objects[].name`);
    if (layer.kind === 'group' && layer.children?.length) collectUntrusted(layer.children, `${path}.children[]`, into);
  }
}

function assets(document: EditorDocument): AssetRef[] {
  const metadata = document.extensions[PUBLIC_DOCUMENT_EXTENSION];
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return [];
  const stored = (metadata as { assets?: unknown }).assets;
  if (!Array.isArray(stored)) return [];
  const imageUsers = new Map<string, LayerId[]>();
  const visit = (group: GroupLayer) => {
    for (const layer of group.children) {
      if (layer.kind === 'image') imageUsers.set(layer.image.asset, [...(imageUsers.get(layer.image.asset) ?? []), layer.id]);
      if (layer.kind === 'group') visit(layer);
    }
  };
  visit(document.root);
  const result: AssetRef[] = [];
  for (const entry of stored as unknown[]) {
    if (!entry || typeof entry !== 'object') continue;
    const asset = entry as { id?: unknown; digest?: unknown; mediaType?: unknown; byteLength?: unknown };
    if (typeof asset.id !== 'string' || typeof asset.mediaType !== 'string' || typeof asset.byteLength !== 'number') continue;
    result.push({
      id: asset.id,
      mediaType: asset.mediaType,
      byteLength: asset.byteLength,
      ...(typeof asset.digest === 'string' && /^sha256:[0-9a-f]{64}$/.test(asset.digest) ? { digest: asset.digest } : {}),
      usedBy: imageUsers.get(asset.id) ?? [],
      locality: 'embedded',
    });
  }
  return result;
}

function fonts(document: EditorDocument, allLayers: readonly Layer[]): FontRef[] {
  const result: FontRef[] = [];
  const metadata = document.extensions[PUBLIC_DOCUMENT_EXTENSION];
  const stored = metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? (metadata as { fonts?: unknown }).fonts : undefined;
  if (Array.isArray(stored)) {
    for (const entry of stored as unknown[]) {
      if (!entry || typeof entry !== 'object') continue;
      const font = entry as { id?: unknown; family?: unknown; weight?: unknown; style?: unknown; source?: { kind?: unknown }; license?: { spdx?: unknown; embedding?: unknown } };
      if (typeof font.family !== 'string' || typeof font.weight !== 'number') continue;
      const style = font.style === 'italic' || font.style === 'oblique' ? font.style : 'normal';
      const embedding = font.license?.embedding;
      result.push({
        ...(typeof font.id === 'string' ? { id: font.id } : {}),
        family: contentLabel(font.family, CONTENT_LIMITS.name),
        weight: font.weight,
        style,
        source: font.source?.kind === 'embedded' ? 'embedded' : 'system',
        available: true,
        ...(typeof font.license?.spdx === 'string' && (embedding === 'installable' || embedding === 'editable' || embedding === 'preview-print' || embedding === 'restricted' || embedding === 'unknown')
          ? { license: { spdx: font.license.spdx, embedding } } : {}),
      });
    }
  }
  const seen = new Set<string>();
  for (const layer of allLayers) {
    if (layer.kind !== 'text' || layer.text.schemaVersion !== 1) continue;
    const key = `${layer.text.style.family}\u0000${layer.text.style.weight}`;
    if (seen.has(key)) continue;
    seen.add(key);
    // Browser text resolves fonts through CSS; phase 0 cannot tell installed from system fonts.
    result.push({ family: contentLabel(layer.text.style.family, CONTENT_LIMITS.name), weight: layer.text.style.weight, style: 'normal', source: 'system', available: !layer.text.missingFont });
  }
  return result;
}

function allLayersOf(group: GroupLayer): Layer[] {
  const result: Layer[] = [];
  for (const layer of group.children) {
    result.push(layer);
    if (layer.kind === 'group') result.push(...allLayersOf(layer));
  }
  return result;
}

/** Projects the live session's document. */
export function projectSceneView(session: DocumentSession, options: SceneViewOptions = {}): SceneView {
  return projectDocumentSceneView(session.document, {
    sessionId: session.sessionId,
    revision: session.currentRevision,
    savedRevision: session.savedRevision,
    dirty: session.dirty,
  }, options);
}

export function projectDocumentSceneView(
  document: EditorDocument,
  state: { sessionId: string; revision: number; savedRevision: number; dirty: boolean },
  options: SceneViewOptions = {},
): SceneView {
  const maxDepth = options.depth ?? Infinity;
  const limit = Math.max(1, Math.floor(options.limit ?? 5_000));
  let scope: GroupLayer = document.root;
  let scopeDepth = 0;
  if (options.root !== undefined) {
    const found = allLayersOf(document.root).find((layer) => layer.id === options.root);
    if (!found || found.kind !== 'group') throw new SceneViewCursorError('Scene view root must be an existing group layer.');
    scope = found;
    const depthOf = (group: GroupLayer, target: LayerId, depth: number): number | null => {
      for (const child of group.children) {
        if (child.id === target) return depth;
        if (child.kind === 'group') {
          const nested = depthOf(child, target, depth + 1);
          if (nested !== null) return nested;
        }
      }
      return null;
    };
    scopeDepth = (depthOf(document.root, found.id, 0) ?? 0) + 1;
  }
  const items: TraversalItem[] = [];
  traverse(scope, scope === document.root ? null : scope.id, scopeDepth, scopeDepth + maxDepth, items);
  const offset = options.cursor ? parseCursor(options.cursor, state.revision) : 0;
  const pageItems = items.slice(offset, offset + limit);
  const views = new Map<LayerId, LayerView>();
  const layers: LayerView[] = [];
  for (const item of pageItems) {
    const view = projectLayer(document, item.layer, item.position, { ...(options.textLimit === undefined ? {} : { textLimit: options.textLimit }), ...(options.provenance ? { provenance: options.provenance } : {}) });
    if (view.kind === 'group' && item.inlineChildren) view.children = [];
    views.set(item.layer.id, view);
    const parent = item.position.parentId === null ? undefined : views.get(item.position.parentId);
    if (parent?.kind === 'group' && parent.children) parent.children.push(view);
    else layers.push(view);
  }
  const untrusted = new Set<string>(['name']);
  collectUntrusted(layers, 'layers[]', untrusted);
  const slots = allLayersOf(document.root).map(slotView).filter((slot): slot is SlotView => Boolean(slot));
  const boards = artboards(document).map((board) => ({ id: board.id, name: contentLabel(board.name, CONTENT_LIMITS.name), rect: { ...board.rect } }));
  const swatches = globalSwatches(document).map((swatch) => ({ id: swatch.id, name: contentLabel(swatch.name, CONTENT_LIMITS.name), color: swatch.color }));
  if (slots.length) { untrusted.add('slots[].name'); if (slots.some((slot) => slot.prompt !== undefined)) untrusted.add('slots[].prompt'); }
  if (boards.length) untrusted.add('artboards[].name');
  if (swatches.length) untrusted.add('swatches[].name');
  const documentFeatures = Object.keys(document.extensions).filter((key) => !MAPPED_DOCUMENT_KEYS.has(key)).map((key) => featureNote(key));
  if (documentFeatures.length) untrusted.add('features[].key');
  const all = allLayersOf(document.root);
  const fontRefs = fonts(document, all);
  if (fontRefs.length) untrusted.add('fonts[].family');
  const nextOffset = offset + pageItems.length;
  return {
    format: SEMANTIC_SCENE_FORMAT,
    version: SEMANTIC_SCENE_VERSION,
    document: options.document ?? 'local',
    documentId: document.id,
    sessionId: state.sessionId,
    revision: state.revision,
    savedRevision: state.savedRevision,
    dirty: state.dirty,
    name: contentLabel(document.name, CONTENT_LIMITS.name),
    canvas: { width: document.width, height: document.height, ppi: document.resolution.x, colorSpace: 'srgb', depth: 8 },
    layers,
    assets: assets(document),
    fonts: fontRefs,
    ...(slots.length ? { slots } : {}),
    ...(boards.length ? { artboards: boards } : {}),
    ...(swatches.length ? { swatches } : {}),
    features: documentFeatures,
    ...(nextOffset < items.length ? { page: { nextCursor: `p1.${state.revision}.${nextOffset}`, omittedLayers: items.length - nextOffset } } : {}),
    untrustedFields: [...untrusted].sort(),
  };
}
